"use client"

import { usePathname, useRouter } from "next/navigation"
import { useCallback, useEffect, useMemo, useRef, useState } from "react"

type Msg = { role: "user" | "assistant"; content: string }
export type ChatViewer = { status: "guest" | "pending" | "approved"; firstName?: string | null; company?: string | null; tier?: string | null }

const STORAGE_KEY = "elevate_chat_session"
const BRAND_KEY = "elevate_chat_session_brand"
const BRAND_PATH = /^\/brand-partnerships(\/|$)/
const BRAND_HASH = "#chat-brand"
// Brand-partnership mode: any button can open it with window.dispatchEvent(new CustomEvent("elevate:open-chat", { detail: { context: "brand_onboarding" } })) or a link to #chat-brand.
const BRAND_GREETING = "Hello, I'm Steve, Elevate's AI assistant. I can talk you through partnering with Elevate and take a few details about your brand so the team can follow up. Where would you like to start?"
const BRAND_OPTIONS = ["How does partnering with Elevate work?", "I'd like to apply", "What do you look for in a brand?", "Speak to the team"]
const BRAND_TEASERS = ["Discuss partnering with Elevate", "Tell Steve about your brand"]
const NUDGE_KEY = "elevate_chat_nudged"
const AVATAR = "/steve/steve-avatar.jpg"
const WELCOME = "/steve/steve-welcome.jpg"
const PHOTO = "[photo] "

/** Shrink a phone photo in the browser (max 1600px JPEG) before it is uploaded. */
async function shrink(file: File): Promise<string> {
  const url = URL.createObjectURL(file)
  try {
    const img = await new Promise<HTMLImageElement>((ok, bad) => {
      const i = new Image()
      i.onload = () => ok(i)
      i.onerror = () => bad(new Error("That photo couldn't be opened. Please try a JPG or PNG."))
      i.src = url
    })
    const scale = Math.min(1, 1600 / Math.max(img.naturalWidth, img.naturalHeight))
    const c = document.createElement("canvas")
    c.width = Math.round(img.naturalWidth * scale)
    c.height = Math.round(img.naturalHeight * scale)
    c.getContext("2d")!.drawImage(img, 0, 0, c.width, c.height)
    return c.toDataURL("image/jpeg", 0.85).split(",")[1]
  } finally {
    URL.revokeObjectURL(url)
  }
}

/**
 * Steve: the same AI assistant who answers Elevate's phone, on the website.
 * Two conversations: before sign-in (discovery, stock, how trade accounts work, get Peter to call) and
 * signed in (reorder, orders, account terms, their prices); approved vs pending accounts get different shortcuts.
 */
function greeting(v: ChatViewer): string {
  const name = v.firstName ? ` ${v.firstName}` : ""
  if (v.status === "approved") return `G'day${name}, Steve here. I can reorder your last order, check an order, look up your prices and stock, or quote delivery. What do you need?`
  if (v.status === "pending") return `Hi${name}, Steve here. Your trade application is with our team. While you wait I can help you find products, check stock or answer questions about trading with us.`
  return "G'day, I'm Steve, Elevate's AI trade assistant. I can help you find products, check what's in stock, explain how trade accounts work, or get Peter, our Sales Director, to call you."
}

function quickOptions(v: ChatViewer, path: string): string[] {
  const onProduct = /^\/products\/[^/]+/.test(path)
  const product = onProduct ? (v.status === "approved" ? ["Is this in stock?", "My price on this for 12?"] : ["Is this in stock?", "How do I get trade pricing on this?"]) : []
  if (v.status === "approved") return [...product, "Reorder my last order", "Where's my order?", "My account terms and credit", "Delivery cost to my postcode", "Find a product", "Talk to Peter"].slice(0, 8)
  if (v.status === "pending") return [...product, "Where's my application up to?", "What will my trade pricing be?", "Find a product", "Delivery to my postcode", "Talk to Peter"].slice(0, 8)
  return [...product, "What brands do you stock?", "How do I open a trade account?", "Minimum order and delivery?", "Check stock on a product", "Do you deliver to my postcode?", "Get Peter to call me"].slice(0, 8)
}

/** Short lines the closed launcher rotates through: what Steve can do for THIS visitor on THIS page. */
function teasers(v: ChatViewer, path: string): string[] {
  const onProduct = /^\/products\/[^/]+/.test(path)
  const first = onProduct ? ["Is this in stock? Ask me"] : []
  if (v.status === "approved") return [...first, "Reorder your last order in one tap", "Your trade price on anything", "Where's my order? I'll check", "Delivery cost to your postcode"]
  if (v.status === "pending") return [...first, "Where's my application up to?", "Find a product for your shelves", "Questions about trading with us?"]
  return [...first, "Check stock in seconds", "How do trade accounts work?", "Find a brand for your shelves", "Delivery to your postcode?"]
}

// The app's known internal paths (matches what tools.ts hands Steve + prompt.ts's own list).
// No shared /au/ prefix to key off any more since the 21 Sep 2026 domain cutover dropped it
// from visible URLs, so this enumerates the real top-level paths instead of one common root.
const APP_PATH = /\/(?:products|collections)\/[a-zA-Z0-9\-\/]*|\/(?:cart|account|privacy-policy)\b/

/** Renders a reply as text: bare app paths (/products/…, /cart, /account, …) become links. Nothing else is interpreted. */
const Line = ({ text }: { text: string }) => {
  const linkRe = new RegExp(`\\[([^\\]]+)\\]\\((${APP_PATH.source})\\)`, "g")
  const clean = text.replace(linkRe, "$2").replace(/\*\*(.+?)\*\*/g, "$1")
  const parts = clean.split(new RegExp(`(${APP_PATH.source})`, "g"))
  return (
    <>
      {parts.map((p, i) =>
        APP_PATH.test(p) ? (
          <a key={i} href={p} className="underline underline-offset-2 hover:text-elevate-gold-dark">
            {p === "/cart" ? "your cart" : p === "/account" ? "your account page" : p === "/privacy-policy" ? "our privacy policy" : p}
          </a>
        ) : (
          <span key={i}>{p}</span>
        )
      )}
    </>
  )
}

const Avatar = ({ size = 32 }: { size?: number }) => (
  // eslint-disable-next-line @next/next/no-img-element
  <img src={AVATAR} alt="" width={size} height={size} className="shrink-0 rounded-full object-cover bg-elevate-cream" style={{ width: size, height: size }} />
)

export default function ChatWidget({ cartId, viewer }: { cartId?: string | null; viewer: ChatViewer }) {
  const path = usePathname() || ""
  const router = useRouter()
  const [open, setOpen] = useState(false)
  const [nudge, setNudge] = useState(false)
  const [messages, setMessages] = useState<Msg[]>([])
  const [input, setInput] = useState("")
  const [busy, setBusy] = useState(false)
  const [showOptions, setShowOptions] = useState(true)
  const [sessionId, setSessionId] = useState<string | null>(null)
  const listRef = useRef<HTMLDivElement>(null)
  const inputRef = useRef<HTMLInputElement>(null)
  const fileRef = useRef<HTMLInputElement>(null)
  const busyRef = useRef(false)
  // Brand-partnership mode: automatic on /brand-partnerships, or forced by the open-chat event / #chat-brand; "retailer" after Steve switches a store owner over
  const [forced, setForced] = useState<"brand" | "retailer" | null>(null)
  const brand = forced ? forced === "brand" : BRAND_PATH.test(path)
  const storeKey = brand ? BRAND_KEY : STORAGE_KEY
  const skipResume = useRef(false)
  const sendRef = useRef<any>(null)
  // "Message Steve" buttons elsewhere (account sidebar, 2 Oct 2026) open this panel; detail.context = "brand_onboarding" opens it in brand mode
  useEffect(() => {
    const openChat = (e: Event) => {
      const d = (e as CustomEvent)?.detail
      if (d?.context === "brand_onboarding" || d?.mode === "brand") setForced("brand")
      setOpen(true)
    }
    const fromHash = () => {
      if (window.location.hash === BRAND_HASH) {
        setForced("brand")
        setOpen(true)
        try {
          window.history.replaceState(null, "", window.location.pathname + window.location.search)
        } catch {}
      }
    }
    window.addEventListener("elevate:open-chat", openChat)
    window.addEventListener("hashchange", fromHash)
    fromHash()
    return () => {
      window.removeEventListener("elevate:open-chat", openChat)
      window.removeEventListener("hashchange", fromHash)
    }
  }, [])
  const options = useMemo(() => (brand ? BRAND_OPTIONS : quickOptions(viewer, path)), [viewer, path, brand])
  const lines = useMemo(() => (brand ? BRAND_TEASERS : teasers(viewer, path)), [viewer, path, brand])
  const [tease, setTease] = useState(0)
  const [unseen, setUnseen] = useState(false)
  useEffect(() => {
    if (open) return
    const t = setInterval(() => setTease((i) => (i + 1) % lines.length), 5000)
    return () => clearInterval(t)
  }, [open, lines.length])
  useEffect(() => {
    try {
      setUnseen(!window.localStorage.getItem("elevate_chat_opened"))
    } catch {}
  }, [])
  const hello = useMemo(() => (brand ? BRAND_GREETING : greeting(viewer)), [viewer, brand])

  // resume an earlier conversation (session id per browser, one per mode)
  useEffect(() => {
    if (skipResume.current) {
      skipResume.current = false
      return
    }
    setMessages([])
    setSessionId(null)
    setShowOptions(true)
    let sid: string | null = null
    try {
      sid = window.localStorage.getItem(storeKey)
    } catch {}
    if (!sid) return
    setSessionId(sid)
    fetch(`/api/chat?session_id=${encodeURIComponent(sid)}`)
      .then((r) => r.json())
      .then((j) => {
        const prior = (j.messages ?? []).filter((m: any) => m.role === "user" || m.role === "assistant").slice(-30)
        if (prior.length) {
          setMessages(prior.map((m: any) => ({ role: m.role, content: m.content })))
          setShowOptions(false)
        }
      })
      .catch(() => {})
  }, [storeKey])

  // a gentle one-time hello on a first visit (not on checkout or account pages)
  useEffect(() => {
    if (/\/(checkout|account)/.test(path)) return
    let seen = true
    try {
      seen = !!window.localStorage.getItem(NUDGE_KEY)
    } catch {}
    if (seen) return
    const t = setTimeout(() => setNudge(true), 12000)
    return () => clearTimeout(t)
  }, [path])

  const dismissNudge = () => {
    setNudge(false)
    try {
      window.localStorage.setItem(NUDGE_KEY, "1")
    } catch {}
  }

  useEffect(() => {
    if (open) {
      setUnseen(false)
      try {
        window.localStorage.setItem("elevate_chat_opened", "1")
      } catch {}
      dismissNudge()
      inputRef.current?.focus()
    }
  }, [open])

  useEffect(() => {
    listRef.current?.scrollTo({ top: listRef.current.scrollHeight, behavior: "smooth" })
  }, [messages, busy, open])

  const send = useCallback(
    async (text: string, sid?: string | null, asBrand: boolean = brand, keepBubble = false) => {
      if (!text.trim() || busyRef.current) return
      if (!keepBubble) setMessages((m) => [...m, { role: "user", content: text }])
      setInput("")
      setShowOptions(false)
      setBusy(true)
      busyRef.current = true
      try {
        const r = await fetch("/api/chat", {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ message: text, session_id: sid === undefined ? sessionId : sid, cart_id: cartId ?? null, page: window.location.pathname, ...(asBrand ? { context: "brand_onboarding" } : {}) }),
        })
        const j = await r.json()
        if (j.session_id) {
          setSessionId(j.session_id)
          try {
            window.localStorage.setItem(asBrand ? BRAND_KEY : STORAGE_KEY, j.session_id)
          } catch {}
        }
        setMessages((m) => [...m, { role: "assistant", content: j.reply ?? j.message ?? "Sorry, something went wrong. Call +61 3 7073 3813 and the team will help." }])
        // Steve judged this visitor to be an existing retailer: carry on in the normal trade chat, answering the same message there
        if (asBrand && j.switch_to === "retailer") {
          skipResume.current = true
          setForced("retailer")
          setSessionId(null)
          busyRef.current = false
          setBusy(false)
          setTimeout(() => sendRef.current?.(text, null, false, true), 0)
          return
        }
        // Steve changed the cart: refresh so the header count and cart page show it
        if ((j.tools ?? []).some((t: string) => t === "add_to_cart" || t === "reorder_last_order")) router.refresh()
      } catch {
        setMessages((m) => [...m, { role: "assistant", content: "I couldn't reach the team just now. Email sales@elevatewholesale.com.au or call +61 3 7073 3813." }])
      } finally {
        setBusy(false)
        busyRef.current = false
        inputRef.current?.focus()
      }
    },
    [sessionId, cartId, router, brand]
  )

  sendRef.current = send

  // A photo (damaged goods etc.): shrink, upload, show it, then tell Steve so he can take it from there.
  const sendPhoto = useCallback(
    async (file: File) => {
      if (busyRef.current) return
      setBusy(true)
      busyRef.current = true
      setShowOptions(false)
      let sid: string | null = null
      try {
        const data = await shrink(file)
        const r = await fetch("/api/chat/photo", {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ session_id: sessionId, content_type: "image/jpeg", data, page: window.location.pathname }),
        })
        const j = await r.json().catch(() => ({}))
        if (!r.ok || !j.url) throw new Error(j.message || "That photo didn't go through. Please email it to sales@elevatewholesale.com.au.")
        sid = j.session_id ?? sessionId
        if (j.session_id) {
          setSessionId(j.session_id)
          try {
            window.localStorage.setItem(STORAGE_KEY, j.session_id)
          } catch {}
        }
        setMessages((m) => [...m, { role: "user", content: PHOTO + j.url }])
      } catch (e: any) {
        setMessages((m) => [...m, { role: "assistant", content: e?.message || "That photo didn't go through. Please email it to sales@elevatewholesale.com.au." }])
        return
      } finally {
        setBusy(false)
        busyRef.current = false
      }
      await send(input.trim() || "I've sent a photo.", sid)
    },
    [sessionId, input, send]
  )

  const newChat = () => {
    setMessages([])
    setSessionId(null)
    setShowOptions(true)
    try {
      window.localStorage.removeItem(storeKey)
    } catch {}
  }

  return (
    <>
      {nudge && !open && (
        <div className="fixed bottom-28 right-4 z-50 max-w-[16rem] rounded-lg border border-elevate-border bg-white p-3 pr-8 shadow-xl text-sm leading-snug">
          <button type="button" onClick={dismissNudge} aria-label="Dismiss" className="absolute right-2 top-1.5 text-lg leading-none text-elevate-grey-mid hover:text-elevate-black">
            ×
          </button>
          <button type="button" onClick={() => setOpen(true)} className="flex items-start gap-2 text-left">
            <Avatar size={32} />
            <span>
              <span className="font-semibold">Steve</span> · {brand ? "Considering partnering with Elevate? I can talk it through." : viewer.status === "approved" ? "Want to reorder or check an order? I can do it here." : "Need a hand finding anything? I can check stock too."}
            </span>
          </button>
        </div>
      )}

      <button
        type="button"
        onClick={() => setOpen((o) => !o)}
        aria-expanded={open}
        aria-controls="elevate-chat-panel"
        aria-label={open ? "Close chat with Steve" : `${brand ? "Discuss with Steve" : "Ask Steve"} · online now: ${lines[tease % lines.length]} (our AI assistant)`}
        className="group fixed bottom-4 right-4 z-50 flex items-center gap-3 rounded-full bg-elevate-black p-1.5 text-white shadow-[0_10px_30px_-8px_rgba(0,0,0,0.55)] transition hover:-translate-y-0.5 motion-reduce:transition-none focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 small:pr-6"
      >
        <span className="relative">
          {open ? (
            <span aria-hidden className="flex h-14 w-14 items-center justify-center text-2xl">×</span>
          ) : (
            <>
              {/* soft pulse: he's here and listening */}
              <span aria-hidden className="absolute inset-0 rounded-full bg-elevate-gold/60 motion-safe:animate-ping [animation-duration:2.5s]" />
              <span className="relative block rounded-full ring-2 ring-elevate-gold">
                <Avatar size={56} />
              </span>
              <span aria-hidden className="absolute bottom-0.5 right-0.5 h-3.5 w-3.5 rounded-full border-2 border-elevate-black bg-emerald-500" />
              {unseen && (
                <span aria-hidden className="absolute -top-1 -right-1 flex h-5 min-w-[1.25rem] items-center justify-center rounded-full bg-elevate-gold px-1 text-xs font-semibold text-elevate-black small:hidden">
                  1
                </span>
              )}
            </>
          )}
        </span>
        {open ? (
          <span className="hidden small:block text-sm font-medium">Close</span>
        ) : (
          <span className="hidden small:flex flex-col items-start text-left leading-tight">
            <span className="text-sm font-semibold">{brand ? "Discuss with Steve" : "Ask Steve"} <span className="font-normal text-neutral-400">· online now</span></span>
            <span key={tease} className="mt-0.5 text-sm font-medium text-elevate-gold">{lines[tease % lines.length]}</span>
          </span>
        )}
      </button>

      {open && (
        <div
          id="elevate-chat-panel"
          role="dialog"
          aria-label="Chat with Steve from Elevate Wholesale"
          className="fixed bottom-24 right-4 z-50 flex h-[min(40rem,calc(100dvh-8rem))] w-[min(26rem,calc(100vw-2rem))] small:h-[min(46rem,calc(100dvh-8rem))] small:w-[min(35rem,calc(100vw-2rem))] flex-col overflow-hidden rounded-xl border border-elevate-border bg-white shadow-2xl"
        >
          <div className="flex items-center gap-3 bg-elevate-black px-4 py-3 text-white">
            <span className="relative">
              <Avatar size={44} />
              <span aria-hidden className="absolute bottom-0 right-0 h-3 w-3 rounded-full border-2 border-elevate-black bg-emerald-500" />
            </span>
            <div className="min-w-0 flex-1">
              <p className="font-heading text-xl leading-none">Steve</p>
              <p className="mt-1 text-xs text-neutral-300">{brand ? "AI assistant · brand partnerships" : "AI trade assistant · replies in seconds"}</p>
            </div>
            <a href="tel:+61370733813" className="hidden small:flex items-center gap-1.5 rounded-full border border-white/30 px-3 py-1.5 text-xs hover:bg-white hover:text-elevate-black" aria-label="Call Steve on +61 3 7073 3813">
              <svg aria-hidden width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M22 16.9v3a2 2 0 0 1-2.2 2 19.8 19.8 0 0 1-8.6-3.1 19.5 19.5 0 0 1-6-6A19.8 19.8 0 0 1 2.1 4.2 2 2 0 0 1 4.1 2h3a2 2 0 0 1 2 1.7c.1.9.4 1.8.7 2.7a2 2 0 0 1-.5 2.1L8 9.8a16 16 0 0 0 6 6l1.3-1.3a2 2 0 0 1 2.1-.5c.9.3 1.8.6 2.7.7a2 2 0 0 1 1.7 2z" /></svg>
              03 7073 3813
            </a>
            {messages.length > 0 && (
              <button type="button" onClick={newChat} className="rounded px-2 py-1 text-xs uppercase tracking-[0.06em] text-neutral-300 hover:text-white">
                New chat
              </button>
            )}
            <button type="button" onClick={() => setOpen(false)} aria-label="Close chat" className="px-1 text-2xl leading-none text-neutral-300 hover:text-white">
              ×
            </button>
          </div>

          <div ref={listRef} className="flex-1 space-y-3 overflow-y-auto bg-elevate-cream/40 px-4 py-4" aria-live="polite">
            {/* welcome card */}
            <div className="flex gap-3 rounded-lg border border-elevate-border bg-white p-3">
              {/* eslint-disable-next-line @next/next/no-img-element */}
              <img src={WELCOME} alt="Steve, Elevate's AI trade assistant" width={96} height={96} className="h-[72px] w-[72px] small:h-24 small:w-24 shrink-0 rounded-lg object-cover" />
              <div className="text-sm leading-relaxed">
                <p>{hello}</p>
                <p className="mt-2 text-xs text-elevate-grey-mid">
                  {brand ? (
                    <>I&apos;m an AI assistant. Our team reviews every brand personally and follows up with you directly. Prefer to talk? Call{" "}
                      <a href="tel:+61370733813" className="font-medium text-elevate-black underline underline-offset-2 whitespace-nowrap">+61 3 7073 3813</a>.</>
                  ) : (
                    <>I&apos;m an AI assistant, and I also answer our customer service line:{" "}
                      <a href="tel:+61370733813" className="font-medium text-elevate-black underline underline-offset-2 whitespace-nowrap">+61 3 7073 3813</a>. Anything I can&apos;t sort, I hand to Peter, our Sales Director.</>
                  )}
                </p>
              </div>
            </div>

            {messages.map((m, i) =>
              m.role === "user" ? (
                <div key={i} className="flex justify-end">
                  {m.content.startsWith(PHOTO + "https://") ? (
                    <a href={m.content.slice(PHOTO.length)} target="_blank" rel="noreferrer" className="max-w-[70%]">
                      {/* eslint-disable-next-line @next/next/no-img-element */}
                      <img src={m.content.slice(PHOTO.length)} alt="Photo you sent" className="max-h-60 rounded-2xl rounded-br-sm border border-elevate-border object-cover" />
                    </a>
                  ) : (
                    <p className="max-w-[85%] small:max-w-[80%] whitespace-pre-wrap rounded-2xl rounded-br-sm bg-elevate-black px-3.5 py-2 text-sm leading-relaxed text-white">{m.content}</p>
                  )}
                </div>
              ) : (
                <div key={i} className="flex items-end gap-2">
                  <Avatar size={28} />
                  <p className="max-w-[85%] small:max-w-[80%] whitespace-pre-wrap rounded-2xl rounded-bl-sm border border-elevate-border bg-white px-3.5 py-2 text-sm leading-relaxed text-elevate-black">
                    <Line text={m.content} />
                  </p>
                </div>
              )
            )}

            {busy && (
              <div className="flex items-end gap-2" role="status" aria-label="Steve is typing">
                <Avatar size={28} />
                <span className="flex gap-1 rounded-2xl rounded-bl-sm border border-elevate-border bg-white px-3.5 py-3">
                  {[0, 150, 300].map((d) => (
                    <span key={d} className="h-1.5 w-1.5 animate-bounce rounded-full bg-elevate-grey-mid" style={{ animationDelay: `${d}ms` }} />
                  ))}
                </span>
              </div>
            )}

            {showOptions && !busy && (
              <div className="flex flex-wrap gap-2 pt-1 small:grid small:grid-cols-2" aria-label="Quick options">
                {options.map((o) => (
                  <button
                    key={o}
                    type="button"
                    onClick={() => send(o)}
                    className="rounded-full border border-elevate-black/70 bg-white px-3 py-1.5 text-sm text-left small:rounded-lg small:px-4 small:py-2.5 hover:bg-elevate-black hover:text-white focus-visible:outline focus-visible:outline-2"
                  >
                    {o}
                  </button>
                ))}
              </div>
            )}
          </div>

          <form
            className="flex items-center gap-2 border-t border-elevate-border bg-white p-3"
            onSubmit={(e) => {
              e.preventDefault()
              send(input)
            }}
          >
            <button
              type="button"
              onClick={() => setShowOptions((s) => !s)}
              aria-pressed={showOptions}
              aria-label="Quick options"
              title="Quick options"
              className="flex h-10 w-10 shrink-0 items-center justify-center rounded-full border border-elevate-border text-lg hover:bg-elevate-grey-light"
            >
              ⚡
            </button>
            {!brand && <button
              type="button"
              onClick={() => fileRef.current?.click()}
              disabled={busy}
              aria-label="Send a photo"
              title="Send a photo (e.g. damaged goods)"
              className="flex h-10 w-10 shrink-0 items-center justify-center rounded-full border border-elevate-border text-lg hover:bg-elevate-grey-light disabled:opacity-40"
            >
              📷
            </button>}
            <input
              ref={fileRef}
              type="file"
              accept="image/*"
              className="hidden"
              onChange={(e) => {
                const f = e.target.files?.[0]
                e.target.value = ""
                if (f) sendPhoto(f)
              }}
            />
            <label htmlFor="elevate-chat-input" className="sr-only">
              Message Steve
            </label>
            <input
              id="elevate-chat-input"
              ref={inputRef}
              value={input}
              onChange={(e) => setInput(e.target.value)}
              placeholder={brand ? "Tell Steve about your brand" : viewer.status === "approved" ? "Ask Steve or type a product and quantity" : "Ask Steve about a product, stock or delivery"}
              maxLength={brand ? 600 : 1000}
              className="min-w-0 flex-1 rounded-full border border-elevate-border px-4 py-2 text-sm focus-visible:outline focus-visible:outline-2"
            />
            <button type="submit" disabled={busy || !input.trim()} className="h-10 shrink-0 rounded-full bg-elevate-black px-4 text-sm text-white disabled:opacity-40">
              Send
            </button>
          </form>
        </div>
      )}
    </>
  )
}
